import { useState, Fragment } from 'react'
// @mui
import {
  Box,
  Stack,
  Button,
  Drawer,
  List,
  Toolbar,
  Typography,
  FormControlLabel,
  Checkbox,
  Collapse,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import cssStyles from '@/utils/cssStyles'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import sortBy from 'lodash/sortBy'
// @types
import { NAVBAR } from '@/config'
// components
import Iconify from '@/components/Iconify'
import Scrollbar from '@/components/Scrollbar'
import { TagsForAllProductsFragment } from '@/generated/graphql'
import useCollapseDrawer from '@/hooks/useCollapseDrawer'
import CollapseButton from './CollapseButton'
import { ListItemStyle, ListItemTextStyle } from '@/components/nav-section/vertical/style'

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}))

type Props = {
  filteredTagIds: string[]
  filterTags?: TagsForAllProductsFragment[]
  isOpen: boolean
  onFilterTagIds?: (tagIds: string[]) => void
  onRemoveTagId?: (tagId: string) => void
  onAddTagId?: (tagId: string) => void
  onOpen: VoidFunction
  onResetAll: VoidFunction
  onClose: VoidFunction
}

export default function ShopFilterSidebar({
  filteredTagIds,
  filterTags,
  isOpen,
  onFilterTagIds,
  onRemoveTagId,
  onAddTagId,
  onResetAll,
  onOpen,
  onClose,
}: Props) {
  const theme = useTheme()

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer()
  const groupByNameTags = groupBy(filterTags, 'groupName')

  const handleSetTag = (tag: TagsForAllProductsFragment, action: 'add' | 'remove') => {
    const url = new URL(window.location.href)
    const tagsParamsString = url.searchParams.get('tags')
    const tagSearch = `${tag.sys.id}_${tag.name}`
    let tagsIdWithNameParams: string[] = []

    if (tagsParamsString) {
      tagsIdWithNameParams = tagsParamsString.split('|').filter((tag) => {
        const tagId = tag.split('_')[0]
        return filterTags?.some((filterTag) => tagId === filterTag.sys.id) ?? false
      })
    }

    if (action === 'add') {
      tagsIdWithNameParams = uniq([...tagsIdWithNameParams, tagSearch])
      onAddTagId?.(tag.sys.id)
    } else {
      tagsIdWithNameParams = uniq(
        tagsIdWithNameParams.filter((tagIdWithName) => tagIdWithName !== tagSearch)
      )
      onRemoveTagId?.(tag.sys.id)
    }
    if (tagsIdWithNameParams.length) {
      url.searchParams.set('tags', tagsIdWithNameParams.join('|'))
    } else {
      url.searchParams.delete('tags')
    }
    window.history.replaceState(null, '', url)
  }

  const handleResetAll = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('tags')
    window.history.replaceState(null, '', url)
    onResetAll?.()
  }

  const sharedBorderRightStyle = {
    borderRight: '1px dashed rgba(145, 158, 171, 0.24)',
  }

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.BASE_COLLAPSE_WIDTH : NAVBAR.BASE_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      <Drawer
        open
        variant="permanent"
        // onMouseEnter={onHoverEnter}
        // onMouseLeave={onHoverLeave}
        PaperProps={{
          sx: {
            width: NAVBAR.BASE_WIDTH,
            borderRightStyle: 'none',
            bgcolor: 'background.default',
            zIndex: 1099,
            transition: (theme) =>
              theme.transitions.create('width', {
                duration: theme.transitions.duration.standard,
              }),
            ...(isCollapse && {
              width: NAVBAR.BASE_COLLAPSE_WIDTH,
            }),
            ...(collapseHover && {
              ...cssStyles(theme).bgBlur(),
              boxShadow: (theme) => theme.customShadows.z24,
            }),
          },
        }}
      >
        <Toolbar />
        <Toolbar />

        <Stack
          direction={isCollapse ? 'column' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1,
            py: 2,
            ...sharedBorderRightStyle,
          }}
        >
          <Typography variant="subtitle1" sx={{ ml: 1, mb: 1 }}>
            Filters
            {isCollapse ? (
              <div>( {filterTags?.length ?? 0} )</div>
            ) : (
              <> ( {filterTags?.length ?? 0} )</>
            )}
          </Typography>
          <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
        </Stack>

        <Scrollbar sx={sharedBorderRightStyle}>
          <Stack
            spacing={3}
            sx={{
              p: 3,
            }}
          >
            <Stack spacing={1}>
              {sortBy(Object.entries(groupByNameTags), [([_, groupedTags]) => groupedTags.length])
                .reverse()
                .map(([groupName, groupedTags]) => (
                  <Fragment key={groupName || 'Others'}>
                    <ListRoot
                      isCollapse={isCollapse ?? false}
                      list={{
                        title: groupName || 'Others',
                        children: groupedTags.map((groupedTag) => ({
                          id: groupedTag.sys.id,
                          title: groupedTag.name ?? '',
                          checked: filteredTagIds.includes(groupedTag.sys.id),
                          onChecked: () => {
                            handleSetTag(
                              groupedTag,
                              filteredTagIds.includes(groupedTag.sys.id) ? 'remove' : 'add'
                            )
                          },
                        })),
                      }}
                    />
                  </Fragment>
                ))}
            </Stack>
          </Stack>
        </Scrollbar>

        <Box
          sx={{
            p: 3,
            ...sharedBorderRightStyle,
          }}
        >
          <Button
            fullWidth
            size="large"
            type="submit"
            color="inherit"
            variant="outlined"
            onClick={handleResetAll}
            startIcon={<Iconify icon={'ic:round-clear-all'} />}
            sx={
              isCollapse
                ? {
                    opacity: 0,
                  }
                : {
                    opacity: 1,
                  }
            }
          >
            Clear All
          </Button>
        </Box>
      </Drawer>
    </RootStyle>
  )
}

type ListRootProps = {
  list: {
    title: string
    children: {
      id: string
      title: string
      checked?: boolean
      onChecked?: () => void
    }[]
  }
  isCollapse: boolean
}

export function ListRoot({ list, isCollapse }: ListRootProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <ItemRoot item={list} isCollapse={isCollapse} open={open} onOpen={() => setOpen(!open)} />

      {!isCollapse && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {list.children.map((item) => (
              <FormControlLabel
                key={item.id}
                sx={{ display: 'block', ml: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={item.checked}
                    onChange={() => item.onChecked?.()}
                  />
                }
                label={item.title}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}

type ItemProps = {
  item: ListRootProps['list']
  isCollapse?: boolean | undefined
  active?: boolean | undefined
  open?: boolean | undefined
  onOpen?: VoidFunction | undefined
  onMouseEnter?: VoidFunction | undefined
  onMouseLeave?: VoidFunction | undefined
}
export function ItemRoot({ item, isCollapse, open = false, active, onOpen }: ItemProps) {
  const { title, children } = item

  const renderContent = (
    <>
      <ListItemTextStyle
        disableTypography
        primary={`${title} ( ${children.length} )`}
        isCollapse={isCollapse}
      />
      {!isCollapse && <>{children && <ArrowIcon open={open} />}</>}
    </>
  )

  return (
    <ListItemStyle onClick={onOpen} activeRoot={active}>
      {renderContent}
    </ListItemStyle>
  )
}

type ArrowIconProps = {
  open: boolean
}

export function ArrowIcon({ open }: ArrowIconProps) {
  return (
    <Iconify
      icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
      sx={{ width: 16, height: 16, ml: 1 }}
    />
  )
}
